import React, { useEffect, useState } from 'react'
import { Box, Button, Chip, Divider, List, ListItem, ListItemText, Typography } from '@mui/material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { impactOf } from './impact'
import { NO_SYSTEM_VALUES } from './systemValueTerms'

/**
 * Historia 176 — RN9b, DC-26 (S97-S104, S105-S113, S152, S194, S199). The impact SHARED by the popover of the
 * usage chip and the confirmation dialog (one component, no copies), in two blocks:
 * - on top, only if the key is in the common structure: T67 "Common structure · visible on every screen" and,
 *   under it, the frame sites where it is used (RN8h, `commonStructureSites`, translated here, no figures);
 * - below, unless the key is ONLY in the common structure (N = 0): T68 "Also in {{screens}} screens of
 *   {{modules}} modules" (T60 without common structure), a chip per module with its number of screens (ordered as
 *   in the menu; never a common-structure chip) and the full screens list, FOLDED, behind T56 "Show the {{count}}
 *   screens" (T57 "Hide" folds it again; screens ordered by name).
 * "Not found where it is used" (T19) when there is nothing at all. Names are read as i18n keys from the artifact
 * and translated here, in the editor's interface language (RN8, CD-16) — including a custom language's own
 * redefinition of a group's name (RN8b).
 */
// DC-28: a tab without a readable title is "Menu › route": the menu keys translated, then the literal route.
const screenLabel = (screen, t_) =>
  screen.keys && screen.keys.length > 0
    ? [...screen.keys.map((key) => t_(key)), ...(screen.literal ? [screen.literal] : [])].join(' › ')
    : screen.route

// V9: the screens list is the ONLY part that scrolls (own overflow and a maximum height tied to the window);
// the summary, the module chips and the buttons stay outside it, so they are always in view.
const SCREENS_LIST_MAX_HEIGHT = '40vh'

const ImpactPanel = ({ termKey = '', artifact = {}, systemValues = NO_SYSTEM_VALUES, onExpandedChange = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const [expanded, setExpanded] = useState(false)
  const impact = impactOf(termKey, artifact, systemValues)

  // V9: when the list unfolds or folds, the host (a popover) re-places itself inside the window.
  useEffect(() => { onExpandedChange(expanded) }, [expanded]) // eslint-disable-line react-hooks/exhaustive-deps

  // Story 204 (RN6.4, RN6.9): a system value is shown wherever it is used: the global warning, never a list of screens.
  if (impact.systemValue) {
    return <Typography data-testid='impact-panel' data-system-value='true'>{t_('This value is shown in every screen that uses it. Apply the change globally?')}</Typography>
  }

  if (impact.screens === 0 && !impact.commonStructure) {
    return <Typography data-testid='impact-panel'>{t_('Not found where it is used')}</Typography>
  }

  return (
    <Box data-testid='impact-panel'>
      {impact.commonStructure
        ? (
          <Box data-testid='impact-common-block'>
            <Typography>{t_('Common structure · visible on every screen')}</Typography>
            {impact.commonSites.map((site) => <Typography key={site} variant='body2'>{t_(site)}</Typography>)}
          </Box>
          )
        : null}

      {impact.commonStructure && impact.screens > 0 ? <Divider sx={{ my: 2 }} /> : null}

      {impact.screens > 0 || !impact.commonStructure
        ? (
          <Box data-testid='impact-screens-block'>
            <Typography>
              {impact.commonStructure
                ? t_('Also in {{screens}} screens of {{modules}} modules', { screens: impact.screens, modules: impact.modules })
                : t_('Affects {{screens}} screens in {{modules}} modules', { screens: impact.screens, modules: impact.modules })}
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 1 }}>
              {impact.groups.map((group) => (
                <Chip
                  key={group.code}
                  size='small'
                  data-testid={`impact-chip-${group.code}`}
                  label={`${t_(group.nameKey)} ${group.screens.length}`}
                />
              ))}
            </Box>

            {impact.screens > 0
              ? (
                <Button size='small' onClick={() => setExpanded(!expanded)}>
                  {expanded ? t_('Hide') : t_('Show the {{count}} screens', { count: impact.screens })}
                </Button>
                )
              : null}

            {expanded
              ? (
                <List data-testid='impact-screens-list' sx={{ overflowY: 'auto', maxHeight: SCREENS_LIST_MAX_HEIGHT }}>
                  {impact.groups.map((group) => (
                    <ListItem key={group.code} data-testid={`impact-group-${group.code}`}>
                      <Box>
                        <Typography variant='subtitle2'>{t_(group.nameKey)}</Typography>
                        <List dense>
                          {group.screens.map((screen) => (
                            <ListItem key={screen.id} data-testid={`impact-screen-${screen.id}`}>
                              <ListItemText primary={screenLabel(screen, t_)} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    </ListItem>
                  ))}
                </List>
                )
              : null}
          </Box>
          )
        : null}
    </Box>
  )
}

export default ImpactPanel
